import React, { useState } from 'react';
import styles from './Avatar.module.css';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface AvatarProps {
  src?: string;
  alt?: string;
  name?: string;
  size?: AvatarSize;
  className?: string;
}

const getInitials = (name: string): string => {
  const words = name.trim().split(' ');
  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const getColorFromName = (name: string): string => {
  const colors = [
    '#2563eb', '#1d4ed8', '#ec4899', '#f43f5e',
    '#f97316', '#eab308', '#22c55e', '#14b8a6',
    '#06b6d4', '#3b82f6',
  ];
  const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[index % colors.length];
};

export const Avatar: React.FC<AvatarProps> = ({
  src,
  alt = 'Avatar',
  name,
  size = 'md',
  className = '',
}) => {
  const [imgError, setImgError] = useState(false);

  const avatarClasses = [styles.avatar, styles[size], className]
    .filter(Boolean)
    .join(' ');

  if (src && !imgError) {
    return (
      <div className={avatarClasses}>
        <img
          src={src}
          alt={alt}
          className={styles.image}
          onError={() => setImgError(true)}
        />
      </div>
    );
  }

  if (name) {
    const initials = getInitials(name);
    const backgroundColor = getColorFromName(name);

    return (
      <div
        className={avatarClasses}
        style={{ backgroundColor }}
        title={name}
      >
        <span className={styles.initials}>{initials}</span>
      </div>
    );
  }

  return (
    <div className={`${avatarClasses} ${styles.placeholder}`}>
      <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        className={styles.placeholderIcon}
      >
        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
      </svg>
    </div>
  );
};

export default Avatar;
